import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import isToday from 'dayjs/plugin/isToday'
import localeData from 'dayjs/plugin/localeData'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { isEmpty, isNumber } from 'lodash'

import i18n from '@/i18n'
import { TARGET_PERIOD_ENTITY_KEYS } from '@/utils/entity-keys'
import { OKR_DATES_SELECT_DATE_PROPS } from '@/utils/okr-element-dates'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(localeData)
dayjs.extend(timezone)
dayjs.extend(duration)
dayjs.extend(isToday)
dayjs.extend(relativeTime)

export const DATES_RANGE_SEPARATOR = '–'

// eslint-disable-next-line import/prefer-default-export
export const getDiffInDays = (start, end) => {
  const date1 = dayjs(start)
  const date2 = dayjs(end)
  const diff = date2.diff(date1, 'days')

  return Math.abs(diff)
}

export const dateFormat = 'DD MMM YYYY'
export const MILESTONE_DAY_FORMAT = 'DD MMM YY'
export const MILESTONE_MONTH_FORMAT = 'MMM YYYY'

export const localDateToUtc = date => {
  if (!date) {
    return date
  }

  return compensateTimeZone(date, true)
}

export const utcDateToLocal = date => {
  if (!date) {
    return date
  }

  return compensateTimeZone(date)
}

export const formatDate = (timestamp, format = dateFormat) => dayjs.utc(timestamp).format(format)

export const addDaysToNow = days => {
  const now = dayjs()
  return now.add(days, 'days').format(dateFormat)
}

export const datepickerLang = () => ({
  days: dayjs.weekdaysShort(),
  months: dayjs.months(),
  monthsShort: dayjs.monthsShort(),
  formatLocale: {
    firstDayOfWeek: 1,
    days: dayjs.weekdaysShort(),
    months: dayjs.months(),
    monthsShort: dayjs.monthsShort()
  },
  placeholder: {
    date: i18n.global.t('datepicker.select_date'),
    dateRange: i18n.global.t('datepicker.select_date_range')
  },
  pickers: [
    i18n.global.t('datepicker.next', { days: 7 }),
    i18n.global.t('datepicker.next', { days: 30 }),
    i18n.global.t('datepicker.previous', { days: 7 }),
    i18n.global.t('datepicker.previous', { days: 30 })
  ]
})

export const getMonthList = () => dayjs.months()

export const getTimeDiffs = (now, targetDate) => {
  const msInHour = 60 * 60 * 1000
  const msInMin = 60 * 1000
  const msInDay = 24 * 60 * 60 * 1000
  return {
    diffForDay: Math.trunc((Number(now) - Number(targetDate)) / msInDay),
    diffForHour: Math.trunc((Number(now) - Number(targetDate)) / msInHour),
    diffForMin: Math.trunc((Number(now) - Number(targetDate)) / msInMin)
  }
}

const DAY_UNIT = 'day'

export const DATE_UNITS = {
  SECOND: 'second',
  DAY: DAY_UNIT,
  WEEK: 'week',
  MONTH: 'month',
  QUARTER: 'quarter',
  YEAR: 'year'
}

export const getLabelForYear = date => {
  if (!date || !dayjs(date).isValid()) {
    throw new Error('Invalid date')
  }

  return dayjs(date).year()
}

export const getLabelForQuarter = date => {
  if (!date || !dayjs(date).isValid()) {
    throw new Error('Invalid date')
  }

  const startDate = dayjs(date)
  return `Q${startDate.quarter()} ${startDate.year()}`
}

export const getValidDueOrStartDate = (date, dateProp = OKR_DATES_SELECT_DATE_PROPS.DUE_DATE) => {
  if (!date) {
    return date
  }

  if (dateProp === OKR_DATES_SELECT_DATE_PROPS.DUE_DATE) {
    return dayjs(date).endOf(DAY_UNIT).millisecond(0)
  } else {
    return dayjs(date).startOf(DAY_UNIT)
  }
}

export const getDisplayedDates = ({ dateFormat = 'DD MMM YYYY', selectedDates } = {}) => {
  if (!dateFormat) {
    return ''
  }
  if (isEmpty(selectedDates)) {
    return ''
  } else {
    const [start, end] = selectedDates
    return {
      start: dayjs(start).format(dateFormat),
      end: dayjs(end).format(dateFormat),
      full: `${dayjs(start).format(dateFormat)} — ${dayjs(end).format(dateFormat)}`
    }
  }
}

export const UNSELECTED_DATE = null

export const compensateTimeZone = (date, toUtc = false) => {
  if (!date || !dayjs(date).isValid()) {
    throw new Error('Invalid date')
  }
  const dateCopy = new Date(+date)
  const minutes = toUtc
    ? dateCopy.getMinutes() - dateCopy.getTimezoneOffset()
    : dateCopy.getMinutes() + dateCopy.getTimezoneOffset()
  return new Date(dateCopy.setMinutes(minutes))
}

export const getStartOfTheDay = date => {
  const dateCopy = new Date(+date)

  dateCopy.setHours(0)
  dateCopy.setMinutes(0)
  dateCopy.setSeconds(0)
  dateCopy.setMilliseconds(0)

  return dateCopy
}

export const getEndOfTheDay = date => {
  const dateCopy = new Date(+date)

  dateCopy.setHours(23)
  dateCopy.setMinutes(59)
  dateCopy.setSeconds(59)
  dateCopy.setMilliseconds(999)

  return dateCopy
}

export const DATE_FILTER_POPUP_STYLE = { left: 'unset', right: '10px' }

export const ELEMENT_PERIODS_DATE_FORMAT = 'YYYY-MM-DD'

export const getDatesInBetween = ({
  startDate,
  endDate,
  step = 1,
  unit = DAY_UNIT,
  format = ELEMENT_PERIODS_DATE_FORMAT
}) => {
  if (
    !startDate ||
    !endDate ||
    !dayjs(startDate).isValid() ||
    !dayjs(endDate).isValid() ||
    startDate === endDate
  ) {
    return []
  }

  const start = dayjs.min(dayjs(startDate), dayjs(endDate)).startOf(DAY_UNIT)
  const end = dayjs.max(dayjs(startDate), dayjs(endDate)).startOf(DAY_UNIT)

  const result = []

  let current = start
  while (current.isBefore(end) || current.isSame(end)) {
    result.push(current.format(format))
    current = current.add(step, unit)
  }

  return result
}

const getDateFromMonthObject = ({ year = 2024, month = 5 } = {}) => {
  if (
    !isNumber(year) ||
    !isNumber(month) ||
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    year < 1971 ||
    year > 2100 ||
    month < 0 ||
    month > 11
  ) {
    throw new Error(`Invalid year: ${year} or month: ${month}`)
  }

  return dayjs().year(year).month(month)
}

export const getMonthStartDateFromObject = ({ year = 2024, month = 5 } = {}) => {
  return getDateFromMonthObject({ year, month }).startOf(DATE_UNITS.MONTH)
}

export const getMonthEndDateFromObject = ({ year = 2024, month = 5 } = {}) => {
  return getDateFromMonthObject({ year, month }).endOf(DATE_UNITS.MONTH)
}

export const getMothObjectFromDate = date => {
  if (!date || !dayjs(date).isValid()) {
    throw new Error('Invalid date')
  }

  return {
    [TARGET_PERIOD_ENTITY_KEYS.YEAR]: dayjs(date).year(),
    [TARGET_PERIOD_ENTITY_KEYS.MONTH]: dayjs(date).month()
  }
}

export const MILESTONES_MIN_DATE = dayjs('1971-01-01').startOf(DAY_UNIT).toDate()
export const MILESTONES_MAX_DATE = dayjs('2037-12-31').endOf(DAY_UNIT).toDate()

export const createDatesRange = (dates = []) =>
  dates.map(v => dayjs.utc(v).format(MILESTONE_DAY_FORMAT)).join(` ${DATES_RANGE_SEPARATOR} `)
